import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import { IMatchState } from '@/store/types'
import store from '@/store'
import {
  MatchEntity,
  MatchAPI,
  MatchGameEntity,
  MatchGameResultEntity,
  JoinableMatchEntity,
  LeagueLobby
} from '@/api/match'
import { TournamentAPI, TournamentEntity } from '@/api/tournament'
import { StageEntity } from '@/api/stage'

@Module({ dynamic: true, store: store, namespaced: true, name: 'match' })
export default class MatchModule extends VuexModule implements IMatchState {
  match: MatchEntity | null = null
  matchGame: MatchGameEntity | null = null
  matchGameResults: MatchGameResultEntity[] = []
  joinableMatchRooms: JoinableMatchEntity[] | null = null
  tournament: TournamentEntity | null = null
  stage: StageEntity | null = null

  matchCoreRulesDialogVisible: boolean = false
  mobileFooterVisible: boolean = true

  // Reset everything

  @Mutation
  RESET_MATCH() {
    this.match = null
    this.matchGame = null
    this.matchGameResults = []
    this.tournament = null
  }

  @Action
  async resetMatchData() {
    this.context.commit('RESET_MATCH')
  }

  /* Get match room details */

  @Mutation
  SET_MATCH(data: MatchEntity) {
    this.match = data
  }

  @Action({ rawError: true })
  async fetchMatch(matchId: string, reload: boolean = false) {
    if (reload) {
      this.context.commit('SET_MATCH', null)
    }

    try {
      const res = await MatchAPI.getMatch(matchId)
      this.context.commit('SET_MATCH', res.data.payload)
    } catch (err) {
      this.context.commit('SET_MATCH', null)
      throw err
    }
  }

  /* Get tournament details */

  @Mutation
  SET_TOURNAMENT(data: TournamentEntity) {
    this.tournament = data
  }

  @Action({ rawError: true })
  async fetchTournament() {
    if (!this.match || !this.match.relatedTournament) {
      this.context.commit('SET_TOURNAMENT', null)
      return
    }

    try {
      const res = await TournamentAPI.getTournament(
        this.match.relatedTournament.tournamentId
      )
      this.context.commit('SET_TOURNAMENT', res.data.payload)
    } catch (err) {
      this.context.commit('SET_TOURNAMENT', null)
      throw err
    }
  }

  /* Get stage details */

  @Mutation
  SET_STAGE(data: StageEntity) {
    this.stage = data
  }

  @Action({ rawError: true })
  async fetchStage() {
    if (!this.match || !this.match.relatedTournament) {
      this.context.commit('SET_STAGE', null)
      return
    }

    try {
      const res = await TournamentAPI.getStage(
        this.match.relatedTournament.stageId
      )
      this.context.commit('SET_STAGE', res.data.payload)
    } catch (err) {
      this.context.commit('SET_STAGE', null)
      throw err
    }
  }

  /* Get match games */

  @Mutation
  SET_MATCH_GAME_RESULTS(data: MatchGameResultEntity[]) {
    this.matchGameResults = data
  }

  @Action({ rawError: true })
  async fetchMatchGameResults(matchGameId: string) {
    this.context.commit('SET_MATCH_GAME_RESULTS', null)
    try {
      const res = await MatchAPI.getMatchGameResults(matchGameId)
      this.context.commit('SET_MATCH_GAME_RESULTS', res.data.payload)
    } catch (err) {
      throw err
    }
  }

  @Action
  async updateMatchGameResults(payload: MatchGameResultEntity[]) {
    this.context.commit('SET_MATCH_GAME_RESULTS', payload)
  }

  /* Get match room games */

  @Mutation
  SET_MATCH_GAME(data: MatchGameEntity) {
    this.matchGame = data
  }

  @Action({ rawError: true })
  async fetchMatchGame(matchGameId: string, reload: boolean = false) {
    if (reload) {
      this.context.commit('SET_MATCH_GAME', null)
    }
    try {
      const res = await MatchAPI.getMatchGame(matchGameId)
      this.context.commit('SET_MATCH_GAME', res.data.payload)
    } catch (err) {
      this.context.commit('SET_MATCH_GAME', null)
      throw err
    }
  }

  /* Get joinable match rooms */

  @Mutation
  SET_JOINABLE_MATCHROOMS(data: JoinableMatchEntity[]) {
    this.joinableMatchRooms = data
  }

  @Action
  async getMemberJoinableMatchRooms(memberId: string = '@me') {
    try {
      const res = await MatchAPI.getMemberJoinableMatchRooms(memberId)
      this.context.commit('SET_JOINABLE_MATCHROOMS', res.data.payload)
    } catch {
      this.context.commit('SET_JOINABLE_MATCHROOMS', null)
    }
  }

  get memberJoinableMatchRooms(): JoinableMatchEntity[] | null {
    return this.joinableMatchRooms
  }

  // Get match room chat message

  @Action({ rawError: true })
  async getChatMessages(page: number) {
    try {
      const res = await MatchAPI.getChatMessages(this.match!.matchId, page)
      return res.data.payload
    } catch (err) {
      throw err
    }
  }

  // Core Rules
  @Mutation
  SET_MATCH_CORE_RULES_DIALOG_VISIBLE(visible: boolean) {
    this.matchCoreRulesDialogVisible = visible
  }

  @Action
  setMatchCoreRulesDialogVisible(visible: boolean) {
    this.context.commit('SET_MATCH_CORE_RULES_DIALOG_VISIBLE', visible)
  }

  get isMatchCoreRulesDialogVisible() {
    return this.matchCoreRulesDialogVisible
  }

  // League lobby
  @Mutation
  SET_LEAGUE_LOBBY(lobby: LeagueLobby | null) {
    if (!this.match) return
    this.match = {
      ...this.match,
      leagueLobby: lobby
    }
  }

  @Action
  setLeagueLobby(lobby: LeagueLobby | null) {
    this.context.commit('SET_LEAGUE_LOBBY', lobby)
  }

  // Match footer visible
  @Mutation
  SET_MATCH_FOOTER_VISIBLE(visible: boolean) {
    this.mobileFooterVisible = visible
  }

  @Action({ rawError: true })
  setMatchFooterVisible(visible: boolean) {
    this.context.commit('SET_MATCH_FOOTER_VISIBLE', visible)
  }
}
